/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import * as z from 'zod';
const numberByAssetSchema = z.record(z.string(), z.number()).nullable();
const booleanByAssetSchema = z.record(z.string(), z.boolean()).nullable();
const numbersByDurationSchema = z.record(z.coerce.number(), z.array(z.number()));
const numbersByAssetAndDurationSchema = z
    .record(z.string(), z.record(z.coerce.number(), z.number()))
    .nullable();
const numberByScenarioAndAssetSchema = z
    .record(z.coerce.number(), z.record(z.string(), z.number()))
    .nullable();
const netBenefitV2FixedPriceSchema = z.strictObject({
    base_rate: z.number(), // $
    escalation_rate: z.number().lt(1), // fraction
});
const netBenefitV2PriceInputsShape = {
    price_id: z.string().nullable(),
    fixed_price: netBenefitV2FixedPriceSchema.nullable(),
};
const hasExactlyOnePriceInput = (value) => (value.price_id === null) !== (value.fixed_price === null);
const netBenefitV2PpaSchema = z.strictObject({
    txm_charges: z.number().nullable(), // $/kW-mo
    txm_charges_escalation_rate: z.number().nullable().optional(), // fraction per year
    txm_sizer: z.string().nullable().optional(), // "poi" or an MV bus wire name
    pv_ppa_price: numberByAssetSchema, // $/MWh per PV asset
    as_available_ppa_price: numberByAssetSchema, // $/MWh per as-available asset
    bess_capacity_price: numbersByAssetAndDurationSchema, // $/MW-month by duration per ESS asset
    thermal_toll_price: numberByAssetSchema.optional(), // $/MW-month per thermal asset
    pv_ppa_price_overrides: numberByScenarioAndAssetSchema.optional(),
    as_available_ppa_price_overrides: numberByScenarioAndAssetSchema.optional(),
    bess_capacity_price_overrides: numberByScenarioAndAssetSchema.optional(),
    thermal_toll_price_overrides: numberByScenarioAndAssetSchema.optional(),
});
const netBenefitV2BtaSchema = z.strictObject({
    txm_capex: z.number().nullable(), // $/kWac
    txm_charges: z.number().nullable().optional(), // $/kW-mo
    txm_charges_escalation_rate: z.number().nullable().optional(), // fraction per year
    txm_sizer: z.string().nullable().optional(), // "poi" or an MV bus wire name
    pv_capex_dc: numberByAssetSchema, // $/kWdc per PV asset
    pv_capex_ac: numberByAssetSchema, // $/kWac per PV asset
    as_available_capex: numberByAssetSchema, // $/kW per as-available asset
    bess_capex: numbersByAssetAndDurationSchema, // $/kWh by duration per ESS asset
    thermal_capex: numberByAssetSchema.optional(), // $/kW per thermal asset
    pv_opex: numberByAssetSchema, // $/kWdc-yr per PV asset
    as_available_opex: numberByAssetSchema, // $/kW-yr per as-available asset
    bess_opex: numbersByAssetAndDurationSchema, // $/kWh-yr by duration per ESS asset
    pv_opex_escalation_rate: numberByAssetSchema,
    as_available_opex_escalation_rate: numberByAssetSchema,
    bess_opex_escalation_rate: numbersByAssetAndDurationSchema,
    capex_overrides: z.record(z.coerce.number(), z.number()).nullable().optional(),
    opex_overrides: z.record(z.coerce.number(), z.array(z.number())).nullable().optional(),
});
const netBenefitV2CapacitySchema = z
    .strictObject({
    ...netBenefitV2PriceInputsShape,
    pv_elcc: z.record(z.string(), z.array(z.number())).nullable(),
    as_available_elcc: z.record(z.string(), z.array(z.number())).nullable(),
    bess_elcc: z.record(z.string(), numbersByDurationSchema).nullable(),
    reserve_margin: z.number().nullable().optional(),
})
    .refine(hasExactlyOnePriceInput, 'Exactly one of price_id or fixed_price is required');
const netBenefitV2RecSchema = z
    .strictObject({
    ...netBenefitV2PriceInputsShape,
    pv_eligible: booleanByAssetSchema,
    as_available_eligible: booleanByAssetSchema,
})
    .refine(hasExactlyOnePriceInput, 'Exactly one of price_id or fixed_price is required');
/**
 * Net benefit request for PlantSim v2 configurations. Mirrors the backend
 * `NetBenefitRequest` model. Per-asset inputs are dicts keyed by the wire
 * `name` of each `mv_bus` asset (e.g. "pv-1"); BESS values are further keyed
 * by duration.
 */
export const netBenefitAnalysisV2RequestSchema = z.strictObject({
    workflow_id: z.string(), // batch workflow
    comment: z.string().nullable().optional(),
    deal_structure: z.enum(['PPA', 'BTA']),
    cost_of_capital: z.number(), // fraction
    ppa: netBenefitV2PpaSchema.nullable(),
    bta: netBenefitV2BtaSchema.nullable(),
    deficit_penalty: z.number().nullable(),
    shape_cost_include_adders: z.boolean().nullable().optional(),
    custom_deficit_metric: z.string().nullable(),
    custom_deficit_penalty: numberByAssetSchema.optional(),
    deficit_market_cost_scalar: z.number().min(0).max(2).nullable().optional(),
    firm_gas_transport_rate: z.number(),
    carbon_cost: z.number(),
    ptc: z
        .strictObject({
        base_rate: z.number(),
        escalation_rate: z.number().max(0.1),
        base_year: z.int(),
        tenor: z.int(),
        pv_eligible: booleanByAssetSchema,
        as_available_eligible: booleanByAssetSchema,
    })
        .nullable(),
    itc: z
        .strictObject({
        rate: z.number().min(0).max(0.5),
        percent_eligible: z.number().min(0).max(1),
        bess_eligible: booleanByAssetSchema,
    })
        .nullable(),
    capacity: netBenefitV2CapacitySchema.nullable(),
    rec: netBenefitV2RecSchema.nullable(),
    market_value: netBenefitV2FixedPriceSchema.nullable(),
    excess_revenue: z.number().nullable(),
    excess_market_revenue_scalar: z.number().min(0).max(2).nullable().optional(),
});
//# sourceMappingURL=net-benefit.js.map